import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { createTipTapDragDropPlugin } from '@/app/utils/client';
import { AgentModelNodeView } from '../components/AgentModelNodeView';

/**
 * AgentModel 节点扩展
 * 支持拖拽和自定义渲染的独立节点类型
 */
export const CustomAgentModel = Node.create({
  name: 'agent-model',
  // 定义为行内节点
  group: 'inline',
  inline: true,
  // 原子节点，不可编辑内部内容
  atom: true,
  // 支持拖拽
  draggable: true,
  addOptions() {
    return {
      // 编辑功能配置
      editable: true, // 是否允许编辑
      editOnDoubleClick: true, // 是否双击编辑
      autoSaveOnBlur: true, // 失去焦点时自动保存
      // 编辑回调
      onBeforeEdit: undefined as ((attrs: Record<string, unknown>) => boolean) | undefined,
      onAfterEdit: undefined as ((attrs: Record<string, unknown>) => void) | undefined,
    };
  },
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) return {};
          return { 'data-id': attributes.id };
        },
      },
      name: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-name'),
        renderHTML: (attributes) => {
          if (!attributes.name) return {};
          return { 'data-name': attributes.name };
        },
      },
      avatar: {
        default: null,
        parseHTML: (element) => {
          const img = element.querySelector('img');
          return img?.getAttribute('src') || null;
        },
        renderHTML: () => {
          // avatar 通过 renderHTML 中的 img 标签渲染
          return {};
        },
      },
      originalDescription: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-original-description'),
        renderHTML: (attributes) => {
          if (!attributes.originalDescription) return {};
          return { 'data-original-description': attributes.originalDescription };
        },
      },
      description: {
        default: null,
        parseHTML: (element) => {
          // 优先从 data-description 属性读取
          const dataDesc = element.getAttribute('data-description');
          return dataDesc;
        },
        renderHTML: (attributes) => {
          // 将 description 作为 data 属性存储，便于准确解析
          if (!attributes.description) return {};
          return { 'data-description': attributes.description };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'span[data-type="agent-model"]',
      },
    ];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          'data-type': 'agent-model',
        },
        HTMLAttributes,
      ),
      ['img', { src: node.attrs.avatar, alt: node.attrs.label }],
      node.attrs.description || node.attrs.label,
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(AgentModelNodeView);
  },
  addProseMirrorPlugins() {
    return [createTipTapDragDropPlugin({ nodeClassName: 'node-agent-model' })];
  },
});
